import { useEffect, useState } from "react";
import { Save, Wallet } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { META } from "../lib/mockData";
import { loadSettings, saveSettings, DEFAULT_SETTINGS } from "../lib/settings";

export default function Settings() {
  const [departments, setDepartments] = useState(META.DEPARTMENTS.join(", "));
  const [floors, setFloors] = useState(String(META.FLOORS.length));
  const [workplaces, setWorkplaces] = useState("300");
  const [defaultRoomCapacity, setDefaultRoomCapacity] = useState("8");
  const [hoursStart, setHoursStart] = useState("08:30");
  const [hoursEnd, setHoursEnd] = useState("19:00");
  const [threshold, setThreshold] = useState("10");
  const [noShowDelay, setNoShowDelay] = useState("10");
  const [granularity, setGranularity] = useState("daily");
  const [costPerDesk, setCostPerDesk] = useState(
    String(DEFAULT_SETTINGS.costPerDesk),
  );
  const [currency, setCurrency] = useState(DEFAULT_SETTINGS.currency);

  useEffect(() => {
    const s = loadSettings();
    setDepartments(s.departments);
    setFloors(String(s.floors));
    setWorkplaces(String(s.workplaces));
    setDefaultRoomCapacity(String(s.defaultRoomCapacity));
    setHoursStart(s.hoursStart);
    setHoursEnd(s.hoursEnd);
    setThreshold(s.threshold);
    setNoShowDelay(s.noShowDelay);
    setGranularity(s.granularity);
    setCostPerDesk(String(s.costPerDesk));
    setCurrency(s.currency);
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    saveSettings({
      departments,
      floors: Number(floors),
      workplaces: Number(workplaces),
      defaultRoomCapacity: Number(defaultRoomCapacity),
      hoursStart,
      hoursEnd,
      threshold,
      noShowDelay,
      granularity,
      costPerDesk: Number(costPerDesk) || DEFAULT_SETTINGS.costPerDesk,
      currency,
    });
    toast.success("Configuration saved", {
      description:
        "Settings stored in your browser. Insights will recalculate using the new cost per desk.",
    });
  };

  return (
    <div data-testid="page-settings">
      <PageHeader
        title="Settings"
        subtitle="Configure measurement scope, working hours and detection rules."
        testId="settings-header"
        actions={
          <Badge variant="secondary" className="bg-blue-50 text-blue-700">
            Demo workspace
          </Badge>
        }
      />

      <form onSubmit={handleSave} className="space-y-6" data-testid="settings-form">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Workspace structure
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="set-departments" className="text-xs font-medium text-slate-500">
                Departments (comma-separated)
              </Label>
              <Input
                id="set-departments"
                value={departments}
                onChange={(e) => setDepartments(e.target.value)}
                className="mt-1"
                data-testid="settings-departments"
              />
              <p className="mt-1 text-xs text-slate-500">
                Currently 8 departments configured.
              </p>
            </div>
            <div>
              <Label htmlFor="set-floors" className="text-xs font-medium text-slate-500">
                Number of floors
              </Label>
              <Input
                id="set-floors"
                type="number"
                min="1"
                value={floors}
                onChange={(e) => setFloors(e.target.value)}
                className="mt-1"
                data-testid="settings-floors"
              />
            </div>
            <div>
              <Label htmlFor="set-workplaces" className="text-xs font-medium text-slate-500">
                Workplace count
              </Label>
              <Input
                id="set-workplaces"
                type="number"
                min="1"
                value={workplaces}
                onChange={(e) => setWorkplaces(e.target.value)}
                className="mt-1"
                data-testid="settings-workplaces"
              />
            </div>
            <div>
              <Label htmlFor="set-room-capacity" className="text-xs font-medium text-slate-500">
                Default meeting room capacity
              </Label>
              <Input
                id="set-room-capacity"
                type="number"
                min="1"
                value={defaultRoomCapacity}
                onChange={(e) => setDefaultRoomCapacity(e.target.value)}
                className="mt-1"
                data-testid="settings-room-capacity"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Working hours & detection rules
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="set-hours-start" className="text-xs font-medium text-slate-500">
                Working hours start
              </Label>
              <Input
                id="set-hours-start"
                type="time"
                value={hoursStart}
                onChange={(e) => setHoursStart(e.target.value)}
                className="mt-1"
                data-testid="settings-hours-start"
              />
            </div>
            <div>
              <Label htmlFor="set-hours-end" className="text-xs font-medium text-slate-500">
                Working hours end
              </Label>
              <Input
                id="set-hours-end"
                type="time"
                value={hoursEnd}
                onChange={(e) => setHoursEnd(e.target.value)}
                className="mt-1"
                data-testid="settings-hours-end"
              />
            </div>
            <div>
              <Label htmlFor="set-threshold" className="text-xs font-medium text-slate-500">
                Occupancy threshold (minutes)
              </Label>
              <Select value={threshold} onValueChange={setThreshold}>
                <SelectTrigger id="set-threshold" data-testid="settings-threshold" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="set-noshow-delay" className="text-xs font-medium text-slate-500">
                No-show delay (minutes)
              </Label>
              <Select value={noShowDelay} onValueChange={setNoShowDelay}>
                <SelectTrigger id="set-noshow-delay" data-testid="settings-noshow" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="20">20 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="set-granularity" className="text-xs font-medium text-slate-500">
                Data granularity
              </Label>
              <Select value={granularity} onValueChange={setGranularity}>
                <SelectTrigger id="set-granularity" data-testid="settings-granularity" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="card-financials">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 font-heading text-lg font-medium text-slate-900">
              <Wallet className="h-4 w-4 text-blue-600" />
              Financial assumptions
            </CardTitle>
            <p className="text-xs text-slate-500">
              Used by the Insights page to compute estimated annual savings.
            </p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">
            <div>
              <Label
                htmlFor="set-cost"
                className="text-xs font-medium text-slate-500"
              >
                Fully loaded annual cost per desk
              </Label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-500">
                  {currency === "EUR"
                    ? "€"
                    : currency === "USD"
                      ? "$"
                      : currency === "GBP"
                        ? "£"
                        : currency}
                </span>
                <Input
                  id="set-cost"
                  type="number"
                  min="0"
                  step="100"
                  value={costPerDesk}
                  onChange={(e) => setCostPerDesk(e.target.value)}
                  className="pl-8"
                  data-testid="settings-cost-per-desk"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Includes rent, utilities, IT, facilities and ancillary services.
              </p>
            </div>
            <div>
              <Label
                htmlFor="set-currency"
                className="text-xs font-medium text-slate-500"
              >
                Currency
              </Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger
                  id="set-currency"
                  data-testid="settings-currency"
                  className="mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-500">
                Display currency for savings on the Insights page.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            type="submit"
            size="lg"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="settings-save-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            Save configuration
          </Button>
        </div>
      </form>
    </div>
  );
}
